import { useState, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';

interface TooltipProps {
  title: string;
  description: string;
  formula?: string;
  children?: React.ReactNode;
}

/**
 * Tooltip component for displaying KPI help information
 * Shows industry definition and calculation formula
 */
export function Tooltip({ title, description, formula, children }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // If not enough space below (< 200px), show above
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setPosition('top');
      } else {
        setPosition('bottom');
      }
    }
  }, [isVisible]);

  // Close tooltip when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        setIsVisible(false);
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible]);

  return (
    <div className="relative inline-flex items-center">
      {children}
      <button
        ref={triggerRef}
        type="button"
        className="ml-1.5 text-zinc-500 hover:text-zinc-300 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-600 rounded-full"
        onClick={() => setIsVisible(!isVisible)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        aria-label={`Help: ${title}`}
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 w-72 p-3 rounded-lg border border-zinc-700 bg-zinc-800 shadow-xl text-left
            ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
            left-1/2 -translate-x-1/2
          `}
          role="tooltip"
        >
          {/* Arrow */}
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-zinc-800 border-zinc-700 rotate-45
              ${position === 'top' ? 'bottom-[-5px] border-r border-b' : 'top-[-5px] border-l border-t'}
            `}
          />

          <h4 className="font-semibold text-white text-sm mb-1.5">{title}</h4>
          <p className="text-xs text-zinc-300 leading-relaxed">{description}</p>

          {formula && (
            <div className="mt-2 pt-2 border-t border-zinc-700">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Formula</p>
              <code className="text-xs text-emerald-400 font-mono bg-zinc-900/50 px-1.5 py-0.5 rounded">
                {formula}
              </code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * KPI help content definitions
 * Centralized place for all KPI explanations
 */
export const KPI_TOOLTIPS = {
  weightedPipeline: {
    title: 'Weighted Pipeline',
    description: 'The total expected revenue from your active deals, adjusted by the probability of closing each deal. Higher probability deals contribute more to this value.',
    formula: 'Σ(Deal Value × Probability %)',
  },
  leadVelocityRate: {
    title: 'Lead Velocity Rate (LVR)',
    description: 'Measures month-over-month growth rate of qualified leads. A leading indicator of future revenue - consistently positive LVR suggests healthy pipeline and likely revenue growth ahead. Particularly useful for SaaS and subscription businesses.',
    formula: '((This Month - Last Month) ÷ Last Month) × 100',
  },
  winRate: {
    title: 'Win Rate',
    description: 'The percentage of closed deals that were won versus lost. A key indicator of sales effectiveness and deal qualification quality.',
    formula: 'Won Deals ÷ (Won + Lost Deals) × 100',
  },
  conversionRate: {
    title: 'Conversion Rate',
    description: 'The percentage of leads that have converted into deals. Measures the effectiveness of your lead nurturing process.',
    formula: 'Converted Leads ÷ Total Leads × 100',
  },
  avgDaysToConvert: {
    title: 'Avg Days to Convert',
    description: 'The average number of days it takes for a lead to convert into a deal. Lower values indicate a more efficient sales process.',
    formula: 'Σ(Deal Created Date - Lead Created Date) ÷ Converted Leads',
  },
  stageVelocity: {
    title: 'Stage Velocity',
    description: 'Average time leads spend in each funnel stage. Helps identify bottlenecks where leads are getting stuck.',
    formula: 'Σ(Exit Date - Entry Date) ÷ Leads in Stage',
  },
  conversionFunnel: {
    title: 'Conversion Funnel',
    description: 'Shows the progression of leads through each stage. Stage-to-stage conversion shows drop-off between stages; cumulative shows overall funnel efficiency.',
    formula: 'Stage Count ÷ Previous Stage Count × 100',
  },
  leadAging: {
    title: 'Lead Aging',
    description: 'Distribution of leads by how long they\'ve been in their current stage. Leads over 30 days may need attention to prevent going stale.',
    formula: 'Today - Last Activity Date',
  },
  activityTrends: {
    title: 'Activity Trends',
    description: 'Daily activity across your funnel including new leads, stage changes, and deals created. Helps identify patterns and seasonality.',
    formula: 'Count per day over selected period',
  },
  pipelineCoverage: {
    title: 'Pipeline Coverage',
    description: 'The ratio of your pipeline value to your revenue target. Industry benchmark is 3-4x coverage to account for deals that won\'t close.',
    formula: 'Pipeline Value ÷ Target (expressed as ratio)',
  },
  repPerformance: {
    title: 'Rep Performance',
    description: 'Individual rep metrics including leads owned, conversion rate, pipeline value, and won revenue. Attribution is based on lead ownership.',
    formula: 'Various metrics per rep',
  },
  slippedDeals: {
    title: 'Slipped Deals',
    description: 'Deals that have passed their expected close date but haven\'t been closed. These require immediate attention to update forecasts.',
    formula: 'Deals where Close Date < Today',
  },
} as const;

export type KpiTooltipKey = keyof typeof KPI_TOOLTIPS;
